import jwtDecode from 'jwt-decode';

import { SESSION_INDEX_KEY, STORAGE_KEYS } from './sessionConstants';
import { getCurrentPrefix, getSessionToken } from './sessionManager';

/**
 * Cross-tab session and tenant context.
 *
 * The access token lives in localStorage and every tab of the session reads it
 * on each request, so switching company in one tab immediately re-points all
 * the others: their next call travels with the new tenant while their Redux
 * state, open screens and joined socket rooms still describe the previous one.
 * Ids from the outgoing tenant then come back as "not found".
 *
 * Tabs therefore watch the token entry and adopt whatever tenant it carries.
 * The token itself is the signal — that keeps every path covered (company
 * switch, environment switch, re-login elsewhere) without a second channel to
 * keep in sync. The entry disappearing is the same story taken to its end: the
 * session is over everywhere, and the tab has to follow it out.
 */

const CONTEXT_TRANSITION_MS = 10_000;

let transitionExpiresAt = 0;

/**
 * Open a window during which requests are expected to fail: those that left
 * before the switch finished propagating still carry ids of the outgoing
 * tenant.
 */
export const beginContextTransition = (durationMs = CONTEXT_TRANSITION_MS) => {
  transitionExpiresAt = Date.now() + durationMs;
};

export const isContextTransitionActive = () => Date.now() < transitionExpiresAt;

/** Tenant ids arrive both upper- and lowercase depending on the source. */
export const isSameContextId = (first, second) =>
  String(first ?? '').toLowerCase() === String(second ?? '').toLowerCase();

const parseRoles = (roles) => {
  if (Array.isArray(roles)) return roles;
  if (typeof roles !== 'string') return null;
  try {
    const parsed = JSON.parse(roles);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Decode the tenant claims of a stored token entry.
 *
 * @param {string|object} rawToken Serialized or parsed `auth-token` entry.
 * @returns {object|null} Tenant context, or null when the token is unreadable.
 */
const readTokenContext = (rawToken) => {
  try {
    const tokenData = typeof rawToken === 'string' ? JSON.parse(rawToken) : rawToken;
    const accessToken = tokenData?.accessToken;
    if (!accessToken) return null;

    const claims = jwtDecode(accessToken) || {};

    return {
      companyId: claims.companyId ?? null,
      companyName: claims.companyCommercialName || claims.companyName || null,
      environmentId: claims.environmentId ?? null,
      environmentName: claims.environmentName ?? null,
      roles: parseRoles(claims.roles),
    };
  } catch (e) {
    return null;
  }
};

/** Tenant context of the token currently held by this session's storage. */
export const getStoredSessionContext = () => {
  try {
    return readTokenContext(getSessionToken());
  } catch (e) {
    return null;
  }
};

/**
 * Notify when another tab of THIS session writes a token, and on every return
 * to visibility.
 *
 * The visibility check is not redundant: a frozen/discarded background tab
 * never receives the `storage` event, so without it such a tab would wake up
 * still rendering the previous tenant.
 *
 * @param {(context: object) => void} handler Receives the stored tenant
 *   context. It is called on any token write, so the handler must ignore
 *   contexts that already match its state (a token refresh rewrites the entry
 *   without changing the tenant).
 * @returns {() => void} Unsubscribe.
 */
export const subscribeToSessionContextChange = (handler) => {
  if (typeof window === 'undefined' || typeof handler !== 'function') return () => {};

  const handleStorage = (event) => {
    try {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      // A missing value is the session ending rather than moving to another
      // tenant, which `subscribeToSessionEnd` takes care of.
      if (!event.key || !event.newValue) return;

      const prefix = getCurrentPrefix();
      // Only this tab's own namespace: another user's session, a portal or an
      // embedded app switching tenants must not drag this tab along.
      if (!prefix || event.key !== `${prefix}${STORAGE_KEYS.AUTH_TOKEN}`) return;

      const context = readTokenContext(event.newValue);
      if (context?.companyId) handler(context);
    } catch (e) {}
  };

  let deferredCheck = null;

  const handleVisibilityChange = () => {
    try {
      if (document.visibilityState !== 'visible') return;
      // A switch this tab is still applying is not a missed one, but dropping
      // the check would also miss a switch made in another tab meanwhile: look
      // again once the transition settles.
      if (isContextTransitionActive()) {
        clearTimeout(deferredCheck);
        deferredCheck = setTimeout(
          handleVisibilityChange,
          Math.max(0, transitionExpiresAt - Date.now()) + 50,
        );
        return;
      }
      const context = getStoredSessionContext();
      if (context?.companyId) handler(context);
    } catch (e) {}
  };

  window.addEventListener('storage', handleStorage);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    clearTimeout(deferredCheck);
    window.removeEventListener('storage', handleStorage);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

/** A session index means this tab believes it holds a session of its own. */
const hasSessionIndex = () => {
  try {
    return !!sessionStorage.getItem(SESSION_INDEX_KEY);
  } catch (e) {
    return false;
  }
};

/**
 * The session this tab is running on no longer exists.
 *
 * Always confirmed against storage instead of trusting the event that led here,
 * so a write that lands in between is not mistaken for a dead session.
 */
const isSessionGone = () => {
  try {
    // This tab is the one tearing the session down; it drives its own exit.
    if (sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) return false;
    if (!hasSessionIndex()) return false;
    return !getSessionToken()?.accessToken;
  } catch (e) {
    return false;
  }
};

/**
 * Notify when the session backing this tab is dropped from storage — a logout,
 * an unrecoverable refresh or an access block, in any tab of the session.
 *
 * The credentials are shared through localStorage, so such an event already
 * unauthenticated this tab: everything it renders belongs to a session the
 * server no longer honours. Nothing signals it on its own, though — the tab
 * only finds out whenever some request happens to come back 401.
 *
 * @param {() => void} handler Called once the session is confirmed gone. It
 *   stops firing after the tab drops its own session index, so the teardown it
 *   performs must clear it (see `cleanupLoginData`).
 * @returns {() => void} Unsubscribe.
 */
export const subscribeToSessionEnd = (handler) => {
  if (typeof window === 'undefined' || typeof handler !== 'function') return () => {};

  const notifyIfGone = () => {
    if (isSessionGone()) handler();
  };

  const handleStorage = (event) => {
    try {
      if (event.storageArea && event.storageArea !== window.localStorage) return;

      // A null key is a whole-store clear. Otherwise only this session's token
      // disappearing counts: a refresh or a tenant switch overwrites the entry
      // and always leaves a value behind.
      if (event.key !== null) {
        if (event.newValue !== null) return;
        const prefix = getCurrentPrefix();
        if (!prefix || event.key !== `${prefix}${STORAGE_KEYS.AUTH_TOKEN}`) return;
      }

      notifyIfGone();
    } catch (e) {}
  };

  const handleVisibilityChange = () => {
    try {
      // A frozen/discarded background tab never receives the `storage` event, so
      // without this it would wake up still showing the ended session.
      if (document.visibilityState === 'visible') notifyIfGone();
    } catch (e) {}
  };

  window.addEventListener('storage', handleStorage);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
